/**
 * Manual Entry Utilities
 *
 * Functions for creating and managing manual data entry in the wizard.
 * When users choose to enter data manually instead of uploading a SIE file,
 * we generate an empty ParsedSieData structure that can be edited in Step 2.
 */

import type { ParsedSieData, BalanceSheet, IncomeStatement, KeyFigures } from "./api";

/**
 * Data source type for tracking how data was entered
 */
export type DataSourceType = "sie" | "manual" | "integration";

/**
 * Creates an empty BalanceSheet structure with zero values
 */
function createEmptyBalanceSheet(): BalanceSheet {
  return {
    assets: {
      fixedAssets: {
        intangible: 0,
        buildings: 0,
        machinery: 0,
        tangible: 0,
        financial: 0,
        total: 0,
      },
      currentAssets: {
        inventory: 0,
        tradeReceivables: 0,
        otherReceivables: 0,
        prepaidExpenses: 0,
        receivables: 0,
        shortTermInvestments: 0,
        cashAndBank: 0,
        total: 0,
      },
      totalAssets: 0,
    },
    equityAndLiabilities: {
      equity: {
        shareCapital: 25000, // Minimum share capital for AB
        unregisteredShareCapital: 0,
        revaluationReserve: 0,
        legalReserve: 0,
        developmentFund: 0,
        sharePremium: 0,
        retainedEarnings: 0,
        currentYearResult: 0,
        details: {
          retainedEarningsOnly: 0,
          groupContributions: 0,
          shareholderContributions: 0,
          ownShares: 0,
          mergerResult: 0,
          prevYearProfitLoss: 0,
          sharePremium2085: 0,
          sharePremiumOld2097: 0,
        },
        total: 25000, // Matches shareCapital
      },
      untaxedReserves: 0,
      provisions: 0,
      longTermLiabilities: 0,
      tradePayables: 0,
      taxLiabilities: 0,
      otherCurrentLiabilities: 0,
      accruedExpenses: 0,
      shortTermLiabilities: 0,
      totalEquityAndLiabilities: 25000, // Matches totalAssets for balance
    },
  };
}

/**
 * Creates an empty IncomeStatement structure with zero values
 */
function createEmptyIncomeStatement(): IncomeStatement {
  return {
    revenue: {
      netSales: 0,
      otherIncome: 0,
      total: 0,
    },
    expenses: {
      goodsAndMaterials: 0,
      otherExternalCosts: 0,
      personnelCosts: 0,
      depreciationAmortization: 0,
      otherOperatingExpenses: 0,
      total: 0,
    },
    operatingResult: 0,
    financialItems: {
      financialIncome: 0,
      financialExpenses: 0,
      total: 0,
    },
    resultAfterFinancialItems: 0,
    extraordinaryItems: 0,
    appropriations: 0,
    taxOnResult: 0,
    netResult: 0,
  };
}

/**
 * Creates empty KeyFigures structure with zero values
 */
function createEmptyKeyFigures(): KeyFigures {
  return {
    totalAssets: 25000, // Matches balance sheet
    equity: 25000,
    adjustedEquity: 25000,
    freeEquity: 0,
    solidityPercent: 100, // 100% solidity when only share capital
    netSales: 0,
    netResult: 0,
  };
}

/**
 * Creates an empty ParsedSieData structure for manual entry.
 * The structure mirrors what the SIE parser would produce, but with zero values.
 *
 * NOTE: This creates a structure that matches BOTH:
 * 1. The API validation schema (for saving to backend)
 * 2. The frontend ParsedSieData type (for display)
 *
 * @param fiscalYearStart - Start date of fiscal year (YYYY-MM-DD)
 * @param fiscalYearEnd - End date of fiscal year (YYYY-MM-DD)
 * @param companyName - Optional company name (for logged-in users, this comes from the report)
 * @param organizationNumber - Optional org number (for logged-in users, this comes from the report)
 */
export function createEmptyParsedSieData(
  fiscalYearStart: string,
  fiscalYearEnd: string,
  companyName?: string,
  organizationNumber?: string
): ParsedSieData & {
  company: { name: string; organizationNumber: string };
  fiscalYears: Array<{ index: number; startDate: string; endDate: string }>;
  metadata: ParsedSieData["metadata"] & { sieType: number; dataSource: DataSourceType };
} {
  // Generate date in YYYYMMDD format
  const generatedDate = new Date().toISOString().split("T")[0].replace(/-/g, "");

  return {
    // API schema expects these fields in metadata
    metadata: {
      // Frontend fields
      format: "SIE4",
      program: "Kvanten",
      programVersion: "1.0",
      generatedDate,
      companyName: companyName || "",
      organizationNumber: organizationNumber || "",
      fiscalYearStart,
      fiscalYearEnd,
      currency: "SEK",
      encoding: "UTF-8",
      // API schema fields
      sieType: 4, // SIE4 format
      // Mark this as manual entry
      dataSource: "manual" as DataSourceType,
    },
    // API schema expects company as separate object
    company: {
      name: companyName || "",
      organizationNumber: organizationNumber || "",
    },
    // API schema expects fiscalYears as array
    fiscalYears: [
      {
        index: 0, // Current year
        startDate: fiscalYearStart,
        endDate: fiscalYearEnd,
      },
    ],
    // Empty raw account data
    accounts: [],
    openingBalances: [],
    closingBalances: [],
    results: [],
    // Aggregated structured data with zero values
    balanceSheet: createEmptyBalanceSheet(),
    incomeStatement: createEmptyIncomeStatement(),
    keyFigures: createEmptyKeyFigures(),
    // No previous year data for manual entry
    previousYearBalanceSheet: null,
    previousYearIncomeStatement: null,
    previousYearKeyFigures: null,
    // Empty validation - will be computed when user edits
    validationIssues: [],
    suggestedEntries: [],
    // Scenario 3 = clean start / new bookkeeping
    bookkeepingScenario: 3,
  };
}

/**
 * Checks if a ParsedSieData structure was created from manual entry
 */
export function isManualEntry(parsedSieData: ParsedSieData | null): boolean {
  if (!parsedSieData) return false;
  return (parsedSieData.metadata as Record<string, unknown>)?.dataSource === "manual";
}

/**
 * Get a display-friendly name for the data source
 */
export function getDataSourceDisplayName(
  parsedSieData: ParsedSieData | null
): string {
  if (!parsedSieData) return "Ingen data";

  const metadata = parsedSieData.metadata as Record<string, unknown>;
  const dataSource = metadata?.dataSource;

  switch (dataSource) {
    case "manual":
      return "Manuell inmatning";
    case "integration":
      return "Integration";
    default:
      return `SIE-fil (${parsedSieData.metadata.program || "Okänt program"})`;
  }
}

/**
 * Format fiscal year for display
 */
export function formatFiscalYear(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  // Check if it's a calendar year
  if (
    start.getMonth() === 0 &&
    start.getDate() === 1 &&
    end.getMonth() === 11 &&
    end.getDate() === 31 &&
    startYear === endYear
  ) {
    return `Räkenskapsår ${startYear}`;
  }

  // Broken fiscal year
  const formatDate = (date: Date) =>
    date.toLocaleDateString("sv-SE", { year: "numeric", month: "short" });

  return `${formatDate(start)} - ${formatDate(end)}`;
}

/**
 * Helper to get a value from a nested object using dot-separated path.
 */
function getNestedValue(obj: Record<string, unknown> | undefined, path: string): unknown {
  if (!obj) return undefined;

  // First try flat key
  if (obj[path] !== undefined) {
    return obj[path];
  }

  // Then try nested path
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Apply user overrides to SIE data.
 * Client-side version of the backend applyOverridesToSieData function.
 *
 * Used for guest mode when calculating triggered notes with user edits.
 *
 * @param sieData - The original ParsedSieData
 * @param overrides - User overrides from Step 2 (balanceSheet/incomeStatement edits)
 */
export function applyOverridesToSieData(
  sieData: ParsedSieData,
  overrides?: Record<string, unknown>
): ParsedSieData {
  if (!overrides) return sieData;

  // Deep clone to avoid mutating original
  const result = JSON.parse(JSON.stringify(sieData)) as ParsedSieData;

  // Helper to get override value - handles both nested and flat structures
  const getOverride = (section: "balanceSheet" | "incomeStatement", path: string): number | undefined => {
    const sectionOverrides = overrides[section] as Record<string, unknown> | undefined;
    const value = getNestedValue(sectionOverrides, path);
    return typeof value === "number" ? value : undefined;
  };

  // Apply balance sheet overrides
  // Assets - fixedAssets
  const intangible = getOverride("balanceSheet", "assets.fixedAssets.intangible");
  if (intangible !== undefined) {
    result.balanceSheet.assets.fixedAssets.intangible = intangible;
  }
  const buildings = getOverride("balanceSheet", "assets.fixedAssets.buildings");
  if (buildings !== undefined) {
    (result.balanceSheet.assets.fixedAssets as Record<string, unknown>).buildings = buildings;
  }
  const machinery = getOverride("balanceSheet", "assets.fixedAssets.machinery");
  if (machinery !== undefined) {
    (result.balanceSheet.assets.fixedAssets as Record<string, unknown>).machinery = machinery;
  }
  const tangible = getOverride("balanceSheet", "assets.fixedAssets.tangible");
  if (tangible !== undefined) {
    result.balanceSheet.assets.fixedAssets.tangible = tangible;
  }
  const financial = getOverride("balanceSheet", "assets.fixedAssets.financial");
  if (financial !== undefined) {
    result.balanceSheet.assets.fixedAssets.financial = financial;
  }

  // Assets - currentAssets
  const inventory = getOverride("balanceSheet", "assets.currentAssets.inventory");
  if (inventory !== undefined) {
    result.balanceSheet.assets.currentAssets.inventory = inventory;
  }
  const receivables = getOverride("balanceSheet", "assets.currentAssets.receivables");
  if (receivables !== undefined) {
    result.balanceSheet.assets.currentAssets.receivables = receivables;
  }
  const shortTermInvestments = getOverride("balanceSheet", "assets.currentAssets.shortTermInvestments");
  if (shortTermInvestments !== undefined) {
    result.balanceSheet.assets.currentAssets.shortTermInvestments = shortTermInvestments;
  }
  const cashAndBank = getOverride("balanceSheet", "assets.currentAssets.cashAndBank");
  if (cashAndBank !== undefined) {
    result.balanceSheet.assets.currentAssets.cashAndBank = cashAndBank;
  }

  // Equity and liabilities
  const shareCapital = getOverride("balanceSheet", "equityAndLiabilities.equity.shareCapital");
  if (shareCapital !== undefined) {
    result.balanceSheet.equityAndLiabilities.equity.shareCapital = shareCapital;
  }
  const retainedEarnings = getOverride("balanceSheet", "equityAndLiabilities.equity.retainedEarnings");
  if (retainedEarnings !== undefined) {
    result.balanceSheet.equityAndLiabilities.equity.retainedEarnings = retainedEarnings;
  }
  const revaluationReserve = getOverride("balanceSheet", "equityAndLiabilities.equity.revaluationReserve");
  if (revaluationReserve !== undefined) {
    result.balanceSheet.equityAndLiabilities.equity.revaluationReserve = revaluationReserve;
  }
  const untaxedReserves = getOverride("balanceSheet", "equityAndLiabilities.untaxedReserves");
  if (untaxedReserves !== undefined) {
    result.balanceSheet.equityAndLiabilities.untaxedReserves = untaxedReserves;
  }
  const provisions = getOverride("balanceSheet", "equityAndLiabilities.provisions");
  if (provisions !== undefined) {
    result.balanceSheet.equityAndLiabilities.provisions = provisions;
  }
  const longTermLiabilities = getOverride("balanceSheet", "equityAndLiabilities.longTermLiabilities");
  if (longTermLiabilities !== undefined) {
    result.balanceSheet.equityAndLiabilities.longTermLiabilities = longTermLiabilities;
  }
  const shortTermLiabilities = getOverride("balanceSheet", "equityAndLiabilities.shortTermLiabilities");
  if (shortTermLiabilities !== undefined) {
    result.balanceSheet.equityAndLiabilities.shortTermLiabilities = shortTermLiabilities;
  }

  // Apply income statement overrides
  const netSales = getOverride("incomeStatement", "revenue.netSales");
  if (netSales !== undefined) {
    result.incomeStatement.revenue.netSales = netSales;
  }
  const otherIncome = getOverride("incomeStatement", "revenue.otherIncome");
  if (otherIncome !== undefined) {
    result.incomeStatement.revenue.otherIncome = otherIncome;
  }
  const goodsAndMaterials = getOverride("incomeStatement", "expenses.goodsAndMaterials");
  if (goodsAndMaterials !== undefined) {
    result.incomeStatement.expenses.goodsAndMaterials = goodsAndMaterials;
  }
  const otherExternalCosts = getOverride("incomeStatement", "expenses.otherExternalCosts");
  if (otherExternalCosts !== undefined) {
    result.incomeStatement.expenses.otherExternalCosts = otherExternalCosts;
  }
  const personnelCosts = getOverride("incomeStatement", "expenses.personnelCosts");
  if (personnelCosts !== undefined) {
    result.incomeStatement.expenses.personnelCosts = personnelCosts;
  }
  const depreciationAmortization = getOverride("incomeStatement", "expenses.depreciationAmortization");
  if (depreciationAmortization !== undefined) {
    result.incomeStatement.expenses.depreciationAmortization = depreciationAmortization;
  }
  const otherOperatingExpenses = getOverride("incomeStatement", "expenses.otherOperatingExpenses");
  if (otherOperatingExpenses !== undefined) {
    result.incomeStatement.expenses.otherOperatingExpenses = otherOperatingExpenses;
  }
  const financialIncome = getOverride("incomeStatement", "financialItems.financialIncome");
  if (financialIncome !== undefined) {
    result.incomeStatement.financialItems.financialIncome = financialIncome;
  }
  const financialExpenses = getOverride("incomeStatement", "financialItems.financialExpenses");
  if (financialExpenses !== undefined) {
    result.incomeStatement.financialItems.financialExpenses = financialExpenses;
  }
  const appropriations = getOverride("incomeStatement", "appropriations");
  if (appropriations !== undefined) {
    result.incomeStatement.appropriations = appropriations;
  }
  const taxOnResult = getOverride("incomeStatement", "taxOnResult");
  if (taxOnResult !== undefined) {
    result.incomeStatement.taxOnResult = taxOnResult;
  }

  // If buildings or machinery were set, recalculate tangible (for manual entry mode)
  // Only do this if tangible wasn't explicitly overridden
  const tangibleOverridden = getOverride("balanceSheet", "assets.fixedAssets.tangible") !== undefined;
  const buildingsVal = (result.balanceSheet.assets.fixedAssets as Record<string, unknown>).buildings as number ?? 0;
  const machineryVal = (result.balanceSheet.assets.fixedAssets as Record<string, unknown>).machinery as number ?? 0;
  if (!tangibleOverridden && (buildings !== undefined || machinery !== undefined)) {
    result.balanceSheet.assets.fixedAssets.tangible = buildingsVal + machineryVal;
  }

  // Recalculate totals for balance sheet
  result.balanceSheet.assets.fixedAssets.total =
    result.balanceSheet.assets.fixedAssets.intangible +
    result.balanceSheet.assets.fixedAssets.tangible +
    result.balanceSheet.assets.fixedAssets.financial;
  result.balanceSheet.assets.currentAssets.total =
    result.balanceSheet.assets.currentAssets.inventory +
    result.balanceSheet.assets.currentAssets.receivables +
    result.balanceSheet.assets.currentAssets.shortTermInvestments +
    result.balanceSheet.assets.currentAssets.cashAndBank;
  result.balanceSheet.assets.totalAssets =
    result.balanceSheet.assets.fixedAssets.total +
    result.balanceSheet.assets.currentAssets.total;

  return result;
}

/**
 * Calculate which notes should be triggered based on SIE data.
 * Client-side version of the backend calculateTriggeredNotes function.
 *
 * This mirrors the logic in apps/api/src/services/annual-report.service.ts
 * and is used for guest mode where no backend call is made.
 *
 * Works with both:
 * - SIE file data (closingBalances exists)
 * - Manual entry (uses aggregated values in balanceSheet/incomeStatement)
 */
export function calculateTriggeredNotes(sieData: ParsedSieData | null): string[] {
  if (!sieData) return [];

  const notes: string[] = [];
  const { balanceSheet, incomeStatement, closingBalances } = sieData;

  // Check if this is manual entry (no account-level data)
  const isManual = !closingBalances || closingBalances.length === 0;

  // Helper to check account balance (returns 0 for manual entry)
  const getAccountBalance = (accountNumber: string): number => {
    if (isManual) return 0;
    const balance = closingBalances.find(
      (b) => b.accountNumber === accountNumber && b.yearIndex === 0
    );
    return balance ? Math.abs(balance.amount) : 0;
  };

  // Helper to sum account range (returns 0 for manual entry)
  const sumAccountRange = (start: number, end: number): number => {
    if (isManual) return 0;
    return closingBalances
      .filter((b) => b.yearIndex === 0)
      .filter((b) => {
        const accNum = parseInt(b.accountNumber);
        return accNum >= start && accNum <= end;
      })
      .reduce((sum, b) => sum + Math.abs(b.amount), 0);
  };

  // Obligatoriska noter (alltid)
  notes.push("ACCOUNTING_PRINCIPLES"); // Redovisningsprinciper
  notes.push("DEPRECIATION_METHODS"); // Avskrivningsgrunder

  // Anläggningstillgångar (om saldo på 10xx-12xx)
  const fixedAssets = balanceSheet.assets.fixedAssets.total;
  if (fixedAssets > 0) {
    notes.push("FIXED_ASSETS");
    // Trigga fråga om avskrivningsmetod och nyttjandeperiod
    notes.push("FIXED_ASSETS_DEPRECIATION_QUESTION");

    // Detaljerad tillgångstyp för avskrivningsfrågor
    // For manual entry, use aggregated values from balanceSheet
    if (isManual) {
      // Use aggregated values
      if (balanceSheet.assets.fixedAssets.intangible > 0) notes.push("HAS_INTANGIBLE_ASSETS");

      // For manual entry, check buildings and machinery separately
      // Now that we have separate input fields in Step 2, these values are set directly
      const buildings = balanceSheet.assets.fixedAssets.buildings ?? 0;
      const machinery = balanceSheet.assets.fixedAssets.machinery ?? 0;

      if (buildings > 0) {
        notes.push("HAS_BUILDINGS");
      }
      if (machinery > 0) {
        notes.push("HAS_MACHINERY");
      }

      // Fallback: if tangible > 0 but no buildings/machinery specified
      // (for backwards compatibility with data before the separate fields existed)
      if (balanceSheet.assets.fixedAssets.tangible > 0 && buildings === 0 && machinery === 0) {
        // When we have tangible but no breakdown, trigger both to be safe
        notes.push("HAS_BUILDINGS");
        notes.push("HAS_MACHINERY");
      }
    } else {
      // Use account-level data
      const intangibleAssets = sumAccountRange(1000, 1099);
      if (intangibleAssets > 0) notes.push("HAS_INTANGIBLE_ASSETS");
      const buildingAssets = sumAccountRange(1100, 1199);
      if (buildingAssets > 0) notes.push("HAS_BUILDINGS");
      const machineryAssets = sumAccountRange(1200, 1299);
      if (machineryAssets > 0) notes.push("HAS_MACHINERY");
    }
  }

  // Goodwill (konto 1070) - ÅRL 5:9
  // For manual entry, this would need to be tracked separately or asked as a question
  const goodwill = getAccountBalance("1070");
  if (goodwill > 0) {
    notes.push("GOODWILL_DEPRECIATION");
  }
  // For manual entry, ask if they have goodwill
  if (isManual && balanceSheet.assets.fixedAssets.intangible > 0) {
    notes.push("GOODWILL_QUESTION");
  }

  // Medelantal anställda (om personalkostnader finns)
  if (incomeStatement.expenses.personnelCosts > 0) {
    notes.push("AVERAGE_EMPLOYEES");
  }

  // Lån till ledande befattningshavare (konto 1680-1689) - ÅRL 5:12
  // For manual entry, always ask as a question
  const loansToBoard = sumAccountRange(1680, 1689);
  if (loansToBoard > 0) {
    notes.push("LOANS_TO_BOARD_MEMBERS");
  }
  if (isManual) {
    notes.push("LOANS_TO_BOARD_QUESTION");
  }

  // Eventualförpliktelser (fråga alltid)
  notes.push("CONTINGENT_LIABILITIES_QUESTION");

  // Ställda säkerheter (om lån finns)
  const hasLoans =
    balanceSheet.equityAndLiabilities.longTermLiabilities > 0 ||
    balanceSheet.equityAndLiabilities.shortTermLiabilities > 0;
  if (hasLoans) {
    notes.push("PLEDGED_ASSETS_QUESTION");
  }

  // Skulder förfaller efter mer än 5 år (om långfristiga skulder finns)
  if (balanceSheet.equityAndLiabilities.longTermLiabilities > 0) {
    notes.push("LIABILITIES_OVER_5_YEARS_QUESTION");
  }

  // Uppskrivningsfond (om konto 2085/2086 har saldo)
  if (balanceSheet.equityAndLiabilities.equity.revaluationReserve > 0) {
    notes.push("REVALUATION_RESERVE");
  }

  // Obeskattade reserver (om 21xx har saldo)
  if (balanceSheet.equityAndLiabilities.untaxedReserves > 0) {
    notes.push("UNTAXED_RESERVES");
  }

  // Händelser efter balansdagen (fråga alltid)
  notes.push("EVENTS_AFTER_BALANCE_DATE_QUESTION");

  // Exceptionella poster (fråga alltid)
  notes.push("EXCEPTIONAL_ITEMS_QUESTION");

  // Transaktioner med närstående (fråga alltid) - ÅRL 5:23, K2 18.10
  // K2-förenkling: endast väsentliga transaktioner utanför normala villkor
  notes.push("RELATED_PARTY_TRANSACTIONS_QUESTION");

  // Moderföretag (fråga alltid om företaget är dotterbolag) - K2 19.2
  notes.push("PARENT_COMPANY_QUESTION");

  // Tjänste-/entreprenaduppdrag
  // For manual entry, check inventory (might include work in progress)
  const workInProgressForOthers = sumAccountRange(1470, 1479);
  if (workInProgressForOthers > 0) {
    notes.push("SERVICE_CONTRACTS_QUESTION");
  }
  // For manual entry, ask if they have service contracts when inventory exists
  if (isManual && balanceSheet.assets.currentAssets.inventory > 0) {
    notes.push("SERVICE_CONTRACTS_QUESTION");
  }

  // Egentillverkade varor (konto 1440-1449) - K2 12.8
  const selfManufacturedGoods = sumAccountRange(1440, 1449);
  if (selfManufacturedGoods > 0) {
    notes.push("SELF_MANUFACTURED_GOODS_QUESTION");
  }
  // For manual entry, ask if they have self-manufactured goods when inventory exists
  if (isManual && balanceSheet.assets.currentAssets.inventory > 0) {
    notes.push("SELF_MANUFACTURED_GOODS_QUESTION");
  }

  // Varulager finns (konto 14xx) - kan behöva uppgifter om värderingsprinciper
  // For manual entry, use aggregated value
  const inventoryTotal = isManual
    ? balanceSheet.assets.currentAssets.inventory
    : sumAccountRange(1400, 1499);
  if (inventoryTotal > 0) {
    notes.push("INVENTORY_EXISTS");
  }

  return notes;
}
